import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  ScanLine, 
  AlertCircle, 
  Info, 
  Github,
  Check,
  ChevronDown,
  Loader2,
  ArrowLeft,
  ExternalLink
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import { useScanStore } from '@/stores/scanStore'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import scanService from '@/services/scans'
import githubService from '@/services/github'
import type { ScanRequest, ScannerType } from '@/types/scan'
import type { GitHubRepository } from '@/services/github'

const scanFormSchema = z.object({
  repository_url: z.string()
    .min(1, 'Repository URL is required')
    .url('Must be a valid URL')
    .refine(
      (url) => {
        try {
          const urlObj = new URL(url)
          return ['github.com', 'gitlab.com', 'bitbucket.org'].some(domain => 
            urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
          )
        } catch {
          return false
        }
      },
      'Must be a valid GitHub, GitLab, or Bitbucket repository URL'
    ),
  branch: z.string().optional(),
  scanners: z.array(z.enum(['trivy', 'hadolint', 'semgrep', 'safety'])).min(1, 'At least one scanner must be selected'),
})

type ScanFormData = z.infer<typeof scanFormSchema>

const availableScanners: Array<{
  id: ScannerType
  name: string
  description: string
  icon: string
  recommended?: boolean
}> = [
  {
    id: 'trivy',
    name: 'Trivy',
    description: 'Vulnerability scanner for containers and dependencies',
    icon: '🛡️',
    recommended: true,
  },
  {
    id: 'hadolint',
    name: 'Hadolint',
    description: 'Dockerfile linter and security checker',
    icon: '🐳',
  },
  {
    id: 'semgrep',
    name: 'Semgrep',
    description: 'Static application security testing (SAST)',
    icon: '🔍',
    recommended: true,
  },
  {
    id: 'safety',
    name: 'Safety',
    description: 'Python dependency vulnerability checker',
    icon: '🐍',
  },
]

export function StartScanPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingRepos, setIsLoadingRepos] = useState(false)
  const [repositories, setRepositories] = useState<GitHubRepository[]>([])
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepository | null>(null)
  const [repoSearchOpen, setRepoSearchOpen] = useState(false)
  const [repoSearchValue, setRepoSearchValue] = useState('')
  const { addScan } = useScanStore()
  const { addToast } = useUIStore()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ScanFormData>({
    resolver: zodResolver(scanFormSchema),
    defaultValues: {
      scanners: ['trivy', 'semgrep'], // Default recommended scanners
    },
  })

  const watchedScanners = watch('scanners', [])
  const watchedUrl = watch('repository_url', '')

  // Check if user is authenticated with GitHub
  const isGitHubAuthenticated = user?.github_id ? true : false

  // Load user's GitHub repositories if authenticated
  useEffect(() => {
    const loadRepositories = async () => {
      if (isGitHubAuthenticated) {
        setIsLoadingRepos(true)
        try {
          const repos = await githubService.getUserRepositories()
          // Sort by updated_at date (most recent first)
          const sortedRepos = repos.sort((a, b) => 
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          )
          setRepositories(sortedRepos)
        } catch (error) {
          console.error('Failed to load GitHub repositories:', error)
          addToast({
            title: 'Failed to load repositories',
            description: 'Could not fetch your GitHub repositories. You can still enter a URL manually.',
            type: 'error',
          })
        } finally {
          setIsLoadingRepos(false)
        }
      }
    }

    loadRepositories()
  }, [isGitHubAuthenticated, addToast])

  const toggleScanner = (scannerId: ScannerType) => {
    const current = watchedScanners || []
    const updated = current.includes(scannerId)
      ? current.filter(id => id !== scannerId)
      : [...current, scannerId]
    setValue('scanners', updated, { shouldValidate: true })
  }

  const onSubmit = async (data: ScanFormData) => {
    setIsSubmitting(true)
    
    try {
      const scanRequest: ScanRequest = {
        repository_url: data.repository_url,
        branch: data.branch || selectedRepo?.default_branch || 'main',
        scanners: data.scanners,
        metadata: {
          user_submitted: true,
          submission_time: new Date().toISOString(),
          repository_name: selectedRepo?.full_name,
        },
      }

      const scan = await scanService.createScan(scanRequest)
      
      addScan(scan)
      addToast({
        title: 'Scan Started',
        description: `Security scan initiated for ${selectedRepo?.full_name || getRepositoryName(data.repository_url)}`,
        type: 'success',
      })

      navigate(`/scan/${scan.id}`)
    } catch (error) {
      console.error('Failed to start scan:', error)
      addToast({
        title: 'Scan Failed',
        description: 'Failed to start the security scan. Please try again.',
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const getRepositoryName = (url: string): string => {
    try {
      const urlObj = new URL(url)
      const pathParts = urlObj.pathname.split('/').filter(Boolean)
      if (pathParts.length >= 2) {
        return `${pathParts[0]}/${pathParts[1].replace(/\.git$/, '')}`
      }
      return url
    } catch {
      return url
    }
  }

  const handleRepoSelect = (repo: GitHubRepository) => {
    setSelectedRepo(repo)
    setValue('repository_url', repo.html_url)
    setValue('branch', repo.default_branch)
    setRepoSearchOpen(false)
  }

  const filteredRepos = repositories.filter(repo => 
    repo.full_name.toLowerCase().includes(repoSearchValue.toLowerCase()) ||
    (repo.description && repo.description.toLowerCase().includes(repoSearchValue.toLowerCase()))
  )

  const isGitHubUrl = watchedUrl.includes('github.com')

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex items-center gap-3">
          <ScanLine className="h-8 w-8 text-blue-600" />
          Start Security Scan
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Analyze your repository for vulnerabilities, secrets, and security issues
        </p>
      </div>

      <Card className="w-full">
        <CardHeader>
          <CardTitle>Configure Your Scan</CardTitle>
          <CardDescription>
            {isGitHubAuthenticated 
              ? "Select a repository from your GitHub account or enter a custom URL"
              : "Enter the repository URL you want to scan"}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Repository Selection/Input */}
            <div className="space-y-2">
              <label htmlFor="repository_url" className="text-sm font-medium">
                Repository
              </label>
              
              {isGitHubAuthenticated ? (
                <div className="space-y-3">
                  {/* Repository Dropdown */}
                  <Popover open={repoSearchOpen} onOpenChange={setRepoSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={repoSearchOpen}
                        className="w-full justify-between"
                        disabled={isLoadingRepos}
                      >
                        {isLoadingRepos ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading repositories...
                          </>
                        ) : selectedRepo ? (
                          <div className="flex items-center gap-2 truncate">
                            <Github className="h-4 w-4" />
                            <span className="truncate">{selectedRepo.full_name}</span>
                          </div>
                        ) : (
                          <>
                            <span>Select a repository from your GitHub account</span>
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-0">
                      <Command>
                        <CommandInput 
                          placeholder="Search repositories..." 
                          value={repoSearchValue}
                          onValueChange={setRepoSearchValue}
                        />
                        <CommandList className="max-h-96 overflow-auto">
                          <CommandEmpty>No repository found.</CommandEmpty>
                          <CommandGroup>
                            {filteredRepos.map((repo) => (
                              <CommandItem
                                key={repo.id}
                                value={repo.full_name}
                                onSelect={() => handleRepoSelect(repo)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedRepo?.id === repo.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{repo.full_name}</span>
                                    {repo.private && (
                                      <Badge variant="secondary" className="text-xs">Private</Badge>
                                    )}
                                    {repo.archived && (
                                      <Badge variant="outline" className="text-xs">Archived</Badge>
                                    )}
                                  </div>
                                  {repo.description && (
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                      {repo.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 mt-1">
                                    {repo.language && (
                                      <span className="text-xs text-gray-500">{repo.language}</span>
                                    )}
                                    <span className="text-xs text-gray-500">⭐ {repo.stargazers_count}</span>
                                    <span className="text-xs text-gray-500">🍴 {repo.forks_count}</span>
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {/* Custom URL Option */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-gray-900 px-2 text-gray-500">Or enter URL manually</span>
                    </div>
                  </div>

                  <div className="relative">
                    <Input
                      id="repository_url"
                      placeholder="https://github.com/username/repository"
                      {...register('repository_url')}
                      className={cn(errors.repository_url && 'border-red-500')}
                    />
                    {isGitHubUrl && (
                      <Github className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <Input
                      id="repository_url"
                      placeholder="https://github.com/username/repository"
                      {...register('repository_url')}
                      className={cn(errors.repository_url && 'border-red-500')}
                    />
                    {isGitHubUrl && (
                      <Github className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Sign in with GitHub prompt */}
                  <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 p-4">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <Info className="inline h-4 w-4 mr-1" />
                      Sign in with GitHub to easily select from your repositories
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => navigate('/login')}
                    >
                      <Github className="h-4 w-4 mr-2" />
                      Sign in with GitHub
                    </Button>
                  </div>
                </div>
              )}

              {errors.repository_url && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.repository_url.message}
                </p>
              )}
              
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Supports public GitHub, GitLab, and Bitbucket repositories
              </p>
            </div>

            {/* Branch */}
            <div className="space-y-2">
              <label htmlFor="branch" className="text-sm font-medium">
                Branch (optional)
              </label>
              <Input
                id="branch"
                placeholder={selectedRepo?.default_branch || "main"}
                {...register('branch')}
              />
              <p className="text-xs text-gray-500">
                Leave empty to scan the default branch
                {selectedRepo && ` (${selectedRepo.default_branch})`}
              </p>
            </div>

            {/* Scanner Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Security Scanners</label>
                <Badge variant="outline" className="text-xs">
                  {watchedScanners.length} selected
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availableScanners.map((scanner) => {
                  const isSelected = watchedScanners.includes(scanner.id)
                  return (
                    <div
                      key={scanner.id}
                      onClick={() => toggleScanner(scanner.id)}
                      className={cn(
                        'relative flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors',
                        isSelected
                          ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                          : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50'
                      )}
                    >
                      <div className="flex items-center h-5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleScanner(scanner.id)}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{scanner.icon}</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {scanner.name}
                          </span>
                          {scanner.recommended && (
                            <Badge variant="success" className="text-xs">
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {scanner.description}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
              
              {errors.scanners && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.scanners.message}
                </p>
              )}
            </div>

            {/* Selected Repository Info */}
            {selectedRepo && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Selected Repository</h4>
                  <a
                    href={selectedRepo.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-500 flex items-center gap-1"
                  >
                    View on GitHub
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Repository:</span>
                    <p className="font-medium">{selectedRepo.full_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Default Branch:</span>
                    <p className="font-medium">{selectedRepo.default_branch}</p>
                  </div>
                  {selectedRepo.language && (
                    <div>
                      <span className="text-gray-500">Language:</span>
                      <p className="font-medium">{selectedRepo.language}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Visibility:</span>
                    <p className="font-medium">{selectedRepo.private ? 'Private' : 'Public'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Starting Scan...
                </>
              ) : (
                <>
                  <ScanLine className="h-4 w-4 mr-2" />
                  Start Security Scan
                </>
              )}
            </Button>

            {/* Estimated Time */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                Estimated scan time: 2-5 minutes
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}